import React, { FC } from 'react';

import { Stack, type SxProps, Tab, Tabs, type Theme } from '@mui/material';

import { SettingsPageHeader } from './SettingsPageHeader';

export type SettingsPageTab = {
  id: string;
  label: string | JSX.Element;
  icon?: JSX.Element;
  showTab?: boolean;
};

export interface SettingsPageShellProps {
  /**
   * The page title rendered in the header.
   */
  title?: string;
  /**
   * An optional icon/image rendered beside the title.
   */
  icon?: React.ReactNode;
  /**
   * An optional description rendered below the title.
   */
  description?: React.ReactNode;
  /**
   * Optional action(s) aligned to the far right of the page title row (e.g. a primary create button).
   */
  headerAction?: React.ReactNode;
  /**
   * Optional tabs rendered below the header. Content switching is the caller's responsibility —
   * the shell simply renders `children` as the active tab body.
   */
  tabs?: SettingsPageTab[];
  /**
   * The currently selected tab id (controlled).
   */
  selectedTab?: string;
  /**
   * Called with the newly selected tab id when a tab is clicked.
   */
  onTabChange?: (value: string) => void;
  /**
   * `sx` applied to the content column. This is the single knob that differentiates the public
   * page shells: full-width table pages pass `{ width: '100%' }`, centered block pages pass a
   * `maxWidth` + `mx: 'auto'`.
   */
  columnSx?: SxProps<Theme>;
  children?: React.ReactNode;
}

/**
 * Private layout primitive shared by {@link SettingsFullWidthPage} and {@link SettingsFixedWidthPage}.
 *
 * Renders the standard settings header (+ optional tabs) and children inside a single content
 * column. Callers control column sizing/centering via `columnSx`; everything else — header,
 * tabs, and vertical rhythm — is identical across both public shells so pages read as one unit.
 */
export const SettingsPageShell: FC<SettingsPageShellProps> = ({
  title,
  icon,
  description,
  headerAction,
  tabs,
  selectedTab,
  onTabChange,
  columnSx,
  children,
}) => (
  <Stack sx={{ height: '100%', flex: 1, width: '100%' }}>
    <Stack spacing={2.5} sx={{ width: '100%', height: '100%', ...columnSx }}>
      <Stack sx={{ mt: 2.5 }} gap={1.5}>
        <SettingsPageHeader icon={icon} title={title} description={description} headerAction={headerAction} />
        {tabs && tabs.length > 0 && (
          <Tabs value={selectedTab} onChange={(_, value) => onTabChange?.(value)}>
            {tabs
              .filter((tab) => tab.showTab !== false)
              .map((tab) => (
                <Tab key={tab.id} label={tab.label} value={tab.id} icon={tab.icon} />
              ))}
          </Tabs>
        )}
      </Stack>
      {children}
    </Stack>
  </Stack>
);
